import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "@/index.css";
import "@/i18n";  // 🟦 V14.4 — Inicialización i18n (es+en, auto-detect navegador).

// 🟥 CUADRO NEGRO DEFINITIVO 2026-06-27 (rev5) — Restauración de los
// mensajes verdes (success / info / message) por orden explícita del
// creador. El "Cuadro Negro #4" que los silenciaba quedó REVERTIDO.
// Sonner ahora renderiza los toasts de éxito normalmente y la utilidad
// bilingüe `showSuccess(key)` (ver /utils/showSuccess.js) los dispara.
// Conservamos abajo el sanitizador de `toast.error` (filtra leaks
// técnicos de stacktrace/exception) — independiente del canal verde.
import { toast as __damyxia_toast_singleton } from "sonner";

// 🟦 CUADRO NEGRO UNIFICADO #2 — Sanitizador global de toast.error.
// Política oficial: SOLO se muestran (a) errores rojos genéricos y (b) advertencias
// amarillas controladas. Cualquier mensaje que filtre stacktrace, request_id,
// nombres de excepciones de librerías (BadRequestError, OpenAIException, litellm,
// HTTPException, etc.) se reemplaza por el mensaje genérico amigable. Mensajes
// ya escritos en español natural por la app (claves i18n, textos cortos) pasan
// intactos.
const __ERROR_TECH_LEAK_HINTS = [
  "badrequesterror",
  "openaiexception",
  "litellm",
  "request_id",
  "traceback",
  "exception",
  "stacktrace",
  "<class '",
  "object at 0x",
  "internal server error",
  "500 internal",
  "502 bad gateway",
  "<!doctype",
  "<html",
  "moderation_blocked", // si llegara aquí, debe usar advertencia amarilla, no error rojo
];
const __GENERIC_ERROR_ES = "Ocurrió un error. Intenta nuevamente.";
const __originalErrorFn = __damyxia_toast_singleton.error?.bind(__damyxia_toast_singleton);
if (typeof __originalErrorFn === "function") {
  __damyxia_toast_singleton.error = (msg, ...rest) => {
    let safe = msg;
    try {
      const str = typeof msg === "string" ? msg : "";
      const low = str.toLowerCase();
      if (
        !str ||
        str.length > 240 ||
        __ERROR_TECH_LEAK_HINTS.some((h) => low.includes(h))
      ) {
        safe = __GENERIC_ERROR_ES;
      }
    } catch {
      safe = __GENERIC_ERROR_ES;
    }
    return __originalErrorFn(safe, ...rest);
  };
}

import { AppRoot as App } from "@/App";
import CookieBanner from "@/components/CookieBanner";
import HydrationGate from "@/components/HydrationGate";
// 🟥 CUADRO NEGRO 2026-06-29 (rev12) — Provider del store global del
// Workspace montado en el ROOT, ENCIMA de <App />. CRÍTICO: el hook
// `useImagePublish` se llama DENTRO del componente App (top-level),
// entonces el Provider DEBE estar más arriba que <App /> para que el
// Context sea visible. Antes lo había puesto dentro del JSX de App →
// App no podía consumir su propio Provider → recibía siempre el stub.
import { WorkspaceImageProvider } from "@/context/WorkspaceImageContext";
import axios from "axios";
import * as Sentry from "@sentry/react";

// 🟥 SPRINT 2 + CUADRO NEGRO MONITOREO (feb-2026) — Sentry frontend.
//
// El Cuadro Negro pide `SENTRY_DSN_FRONTEND` y `environment="production"`
// con `tracesSampleRate=0.2`. Mantenemos compatibilidad con la variable
// previa (`REACT_APP_SENTRY_DSN`) para no romper builds ya configurados,
// dando prioridad a la nueva.
// Si el DSN está vacío, Sentry NO se inicializa y todas las llamadas a
// `Sentry.captureException`/`addBreadcrumb` son no-ops seguros.
const SENTRY_DSN =
  process.env.REACT_APP_SENTRY_DSN_FRONTEND ||
  process.env.SENTRY_DSN_FRONTEND ||
  process.env.REACT_APP_SENTRY_DSN;
const SENTRY_ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || "production";
const SENTRY_TRACES_RATE = parseFloat(
  process.env.REACT_APP_SENTRY_TRACES_RATE || "0.2",
);

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.captureConsoleIntegration({ levels: ["error"] }),
    ],
    tracesSampleRate: SENTRY_TRACES_RATE,
    environment: SENTRY_ENV,
    sendDefaultPii: false,
    // Filtro: scrubbing de URLs con tokens/passwords obvios.
    beforeSend(event) {
      try {
        if (event.request?.url) {
          event.request.url = event.request.url
            .replace(/([?&](token|api_key|password)=)[^&]+/gi, "$1[Filtered]");
        }
        if (event.request?.headers) {
          ["Cookie", "Authorization", "X-Api-Key"].forEach((h) => {
            if (event.request.headers[h]) event.request.headers[h] = "[Filtered]";
          });
        }
      } catch (_) { /* fail-open */ }
      return event;
    },
    // Errores ruidosos de terceros (PostHog, extensiones) que ya silenciamos.
    ignoreErrors: [
      "DataCloneError",
      "PerformanceServerTiming",
      "could not be cloned",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
    ],
  });
}

// Cookie httpOnly: asegurar que fetch y axios envíen cookies automáticamente
const _originalFetch = window.fetch;
window.fetch = function(url, options = {}) {
  return _originalFetch(url, { ...options, credentials: 'include' });
};
axios.defaults.withCredentials = true;

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    {/* 🟦 SPRINT 3 — HelmetProvider envuelve toda la app para que las
        páginas públicas (Marketplace, RecipeDetail) puedan inyectar
        <title>, <meta>, OpenGraph tags dinámicamente. */}
    <HelmetProvider>
      <WorkspaceImageProvider>
        <App />
        {/* 🟦 CUADRO NEGRO 2026-02-26 — Banner de cookies minimalista.
            Se monta a nivel root (fuera de App) para que sea independiente del
            árbol autenticado y persistente entre cambios de ruta. */}
        <CookieBanner />
      </WorkspaceImageProvider>
    </HelmetProvider>
  </React.StrictMode>,
);
